// connectedState.js — interpreta a CONECTIVIDADE de um provider/adapter num estado
// RICO em vez do boolean "tem chave". Mata o falso-verde da classe #645: chave
// presente MAS a conta sem acesso ao modelo (403) mostrava "connected" (verde). O
// probe real (/api/adapters/:id/test → testAdapter no Worker) devolve "HTTP 403 · …";
// aqui a gente classifica e cacheia (TTL 10min) pra o dot dizer a VERDADE. PURO fora
// do cache (Date.now só p/ o TTL). Fora de loop/ de propósito (usa Date.now).

export const CONN = Object.freeze({
  LOCAL: 'local', // adapter local (Ollama/LM Studio) ativo — sem chave, mas alcançável
  CONNECTED: 'connected', // probe passou (ou, sem probe, chave presente = otimista)
  NO_KEY: 'no_key', // sem chave configurada
  NO_ACCESS: 'no_access', // 403 — a CONTA não tem acesso ao modelo (o caso gpt-5.6-sol)
  BAD_KEY: 'bad_key', // 401 — chave inválida/expirada
  RATE_LIMITED: 'rate_limited', // 429
  UNREACHABLE: 'unreachable', // rede / 5xx / timeout / erro sem status
});

const REACHABLE = new Set([CONN.CONNECTED, CONN.LOCAL]);
/** Só isto é "verde de verdade" no dot. */
export const isReachable = (s) => REACHABLE.has(s);

// Mapa adapterName→provider dos built-ins cujo slug difere da chave do registry
// (openai-api≠openai etc.) — a RAIZ do drift do #645. Vive aqui, na camada de
// modelos, como FONTE ÚNICA (antes estava solto dentro do hook useUnifiedModels).
export const ADAPTER_NAME_TO_PROVIDER = Object.freeze({
  'openai-api': 'openai',
  'anthropic-api': 'anthropic',
  gemini: 'google',
});
/** provider canônico de um adapter (aplica o alias acima). */
export const providerOfAdapter = (a) => {
  const raw = (a && (a.provider || a.name)) || '';
  return ADAPTER_NAME_TO_PROVIDER[raw] || raw || null;
};

/** 1 frase humana por estado (título/aria do dot). */
export const CONN_LABEL = Object.freeze({
  [CONN.LOCAL]: 'Local — ativo',
  [CONN.CONNECTED]: 'Conectado',
  [CONN.NO_KEY]: 'Sem chave configurada',
  [CONN.NO_ACCESS]: 'Sem acesso — a conta não tem esse modelo (403)',
  [CONN.BAD_KEY]: 'Chave inválida ou expirada (401)',
  [CONN.RATE_LIMITED]: 'Limite de requisições atingido (429)',
  [CONN.UNREACHABLE]: 'Inalcançável (rede/servidor)',
});

/** status HTTP → estado. */
export function classifyStatus(status) {
  const s = Number(status);
  if (s >= 200 && s < 300) return CONN.CONNECTED;
  if (s === 401) return CONN.BAD_KEY;
  if (s === 403) return CONN.NO_ACCESS;
  if (s === 429) return CONN.RATE_LIMITED;
  return CONN.UNREACHABLE; // 4xx restante, 5xx, 0
}

/**
 * Classifica o resultado do /api/adapters/:id/test (testAdapter):
 * { ok:true } → connected; { ok:false, error:"HTTP 403 · …" } → extrai o status.
 * @param {{ok?:boolean, error?:string}|null} res
 */
export function classifyProbeResult(res) {
  if (!res) return CONN.UNREACHABLE;
  if (res.ok) return CONN.CONNECTED;
  const m = /HTTP\s+(\d{3})/.exec(String(res.error || ''));
  if (m) return classifyStatus(Number(m[1]));
  return CONN.UNREACHABLE; // erro de rede sem status (fetch throw)
}

/**
 * Estado OTIMISTA sem probe: local→local, chave→connected, senão no_key.
 * @param {{ isLocal?:boolean, hasKey?:boolean, active?:boolean }} p
 */
export function baselineState({ isLocal, hasKey, active = true } = {}) {
  if (isLocal) return active ? CONN.LOCAL : CONN.NO_KEY;
  return hasKey ? CONN.CONNECTED : CONN.NO_KEY;
}

// ── Cache do probe (TTL 10min) por chave (id do adapter/provider) ────────────────
// `now` é injetável p/ teste determinístico. writeProbeCache é chamado pelo dono do
// /test; resolveConnState sobrepõe o baseline com o probe fresco quando existe.
const TTL_MS = 600_000;
const cache = new Map(); // key → { state, detail, ts }

export function writeProbeCache(key, state, detail = null, now = Date.now()) {
  if (!key) return null;
  const entry = { state, detail, ts: now };
  cache.set(key, entry);
  return entry;
}

export function readProbeCache(key, now = Date.now()) {
  const hit = key ? cache.get(key) : null;
  if (!hit || now - hit.ts > TTL_MS) return null;
  return hit;
}

/**
 * Estado final do dot: probe fresco (se houver) sobrepõe o baseline otimista.
 * @param {{ key?:string, isLocal?:boolean, hasKey?:boolean, active?:boolean, now?:number }} p
 */
export function resolveConnState({ key, isLocal, hasKey, active, now = Date.now() } = {}) {
  const probed = readProbeCache(key, now);
  if (probed) return probed.state;
  return baselineState({ isLocal, hasKey, active });
}

/** Test-only. */
export function __resetConnCache() { cache.clear(); }
